import React, { useEffect } from "react";

import { useUniqueId, useChangeHandler, useLocales } from "hooks";
import { Option } from "components";

interface Props {
  label: string;
  options: Array<Option>;
  multiple?: boolean;
  onChange: (ids: Array<number>) => void;
}

export const Select: React.FC<Props> = ({
  label,
  options,
  multiple = false,
  onChange,
}) => {
  const id = useUniqueId();
  const selector = `select#${id}`;
  const { t } = useLocales();

  useChangeHandler<string | Array<string>>(selector, (names) => {
    if (typeof names === "string") names = [names];
    const ids = names.map(
      (value: string) => options.find(({ label }) => label === value)?.id
    ) as Array<number>;
    onChange(ids);
  });

  useEffect(() => {
    window.$(selector).chosen({
      disable_search_threshold: 10,
      width: "auto",
      no_results_text: t("history.form.select.noResults"),
    });

    window
      .$(selector)
      .next(".chosen-container")
      .find("input")
      .attr("name", label)
      .attr("aria-label", label);

    return () => {
      window.$(selector).chosen("destroy");
    };
  }, [label, selector, t]);

  useEffect(() => {
    window.$(selector).trigger("chosen:updated");
  }, [options, selector]);

  return (
    <div className="select-component">
      <label htmlFor={label} className="control-label">
        {label}
      </label>
      <select
        id={id}
        multiple={multiple && ("multiple" as unknown as boolean)}
        className="form-control"
        data-placeholder={t("history.form.select.placeholder")}
      >
        {options.map((option) => (
          <option key={option.id}>{option.label}</option>
        ))}
      </select>
    </div>
  );
};
